'use client';

import { NetworkAvatar } from '#/components/legacy/network-avatar';
import { Button } from '#/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import { Input } from '#/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select';
import { NetworkRenderSWR } from '#/components/v2/network-render-swr';
import { ResponseCode } from '#/constants';
import { useTranslation } from '#/contexts/useTranslation';
import useWatcherAddressUpdateMutation from '#/data/useWatcherAddressUpdateMutation';
import {
  WatcherAddressInfo,
  useMutateWatcherAddressList,
} from '#/data/v1/useWatcherAddressList';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

interface Props {
  originalRow: WatcherAddressInfo;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  /**
   * DialogTrigger
   */
  children?: ReactNode;
}
export function TableRowActionsEditDialog({
  originalRow,
  open,
  onOpenChange,
  children,
}: Props) {
  const translation = useTranslation(
    translation =>
      translation.PortfolioDashboard.ManageYourAddresses.EditAddress
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent className="sm:max-w-(--breakpoint-sm)">
        <DialogHeader>
          <DialogTitle>{translation.Title}</DialogTitle>
          {/* <DialogDescription>
            To view your portfolio, rewards and balance, please add your wallet
            address for the network you have staked.
          </DialogDescription> */}
        </DialogHeader>
        <TableRowActionsEditDialogForm
          originalRow={originalRow}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  );
}

interface TableRowActionsEditDialogFormProps {
  originalRow: WatcherAddressInfo;
  onOpenChange?(open: boolean): void;
}
const TableRowActionsEditDialogForm: React.FC<
  TableRowActionsEditDialogFormProps
> = ({ originalRow, onOpenChange }) => {
  const translation = useTranslation(
    translation => translation.PortfolioDashboard.AddAddress
  );
  const translationApplication = useTranslation(
    translation => translation.Application
  );
  const formSchema = useMemo(
    () =>
      z.object({
        symbol: z.string(),
        address: z.string().trim(),
        label: z
          .string()
          .trim()
          .max(10, translation.AddressName.ErrorMessage)
          .optional(),
      }),
    [translation]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      symbol: originalRow.symbol,
      label: originalRow.label,
      address: originalRow.address,
    },
  });

  const { trigger: triggerUpdate, isMutating } =
    useWatcherAddressUpdateMutation();

  const onMutateWatcherAddressList = useMutateWatcherAddressList();

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { symbol, label, address } = values;

    const res = await triggerUpdate({
      symbol,
      address,
      label,
    });
    if (res?.data.code === ResponseCode.SUCCESS) {
      onOpenChange?.(false);
      onMutateWatcherAddressList();

      toast.success('Address updated successfully.');
    } else {
      toast.error(res?.data.message);
    }
  }
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-x-2 gap-y-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="symbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translation.Network.Label}</FormLabel>
                  <Select
                    disabled
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select network" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <NetworkRenderSWR>
                        {({ data, isLoading }) =>
                          isLoading ? (
                            <div className="h-24 flex items-center justify-center">
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                          ) : (
                            data?.data.data?.map(item => (
                              <SelectItem key={item.symbol} value={item.symbol}>
                                <NetworkAvatar
                                  symbol={item.symbol}
                                  className="mr-2 w-4 h-4 inline-block"
                                />
                                {item.name} ({item.symbol})
                              </SelectItem>
                            ))
                          )
                        }
                      </NetworkRenderSWR>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {translation.AddressName.Label}{' '}
                    <span className="text-xs text-gray-500">
                      {translation.AddressName.LabelOptional}
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translation.AddressName.Placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{translation.Address.Label}</FormLabel>
                <FormControl>
                  <Input disabled placeholder="Enter your address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">
                {translationApplication.Dialog.CancelButtonLabel}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {translationApplication.Dialog.SubmitButtonLabel}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
};
